/**
 * Plays the raid-start sting when the player launches a raid.
 *
 * "Launches" is the narrow reading on purpose: the store also flips to ACTIVE
 * when a page reload restores a raid already in progress, and a drum roll on
 * every refresh would be noise. `startRaid` stamps `startedAt` with the client
 * clock, while a restore stamps the server's, so a fresh start is the one that
 * is under a few seconds old when the raid id first appears.
 */
import { raidStateStore } from '../state/raid-state';
import type { MixerHandle } from './mixer';
import { TRACKS } from './tracks';

/** A restored raid started long ago; a launch started a moment ago. */
const FRESH_MS = 5_000;

export function createRaidStartSound(mixer: MixerHandle): () => void {
  void mixer.load(TRACKS.raidStart.path).catch(() => {
    // Pre-warm only; the play path retries and a silent launch is acceptable.
  });

  let lastRaidId: string | null = raidStateStore.get().raidId;

  return raidStateStore.subscribe((s) => {
    const previous = lastRaidId;
    lastRaidId = s.raidId;
    if (s.raidId === null || s.raidId === previous) return;
    if (s.status !== 'ACTIVE') return;
    if (Date.now() - s.startedAt > FRESH_MS) return;
    if (!mixer.isRunning()) return;
    void mixer.playOnce(TRACKS.raidStart.path, TRACKS.raidStart.channel).catch(() => {
      // Never throw inside a store listener — it would starve the other subscribers.
    });
  });
}
